import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FilePlus, Download, Mail, CheckCircle, Trash2, Search, Filter, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol, formatCurrency } from '../utils/currency';
import InvoiceModal from '../components/modals/InvoiceModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchInvoices = React.useCallback(async () => {
    try {
      const res = await apiClient.get('/invoices');
      setInvoices(res.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = React.useCallback(() => {
    let filtered = [...invoices];
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }
    if (searchQuery.trim()) {
      filtered = filtered.filter(inv =>
        inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.notes?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredInvoices(filtered);
  }, [invoices, statusFilter, searchQuery]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSendEmail = async (invoiceId) => {
    const promise = apiClient.post(`/invoices/${invoiceId}/send`);
    toast.promise(promise, {
      loading: 'Sending invoice...',
      success: 'Invoice sent to client email!',
      error: 'Failed to send invoice'
    });
    await promise;
    fetchInvoices();
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await apiClient.patch(`/invoices/${invoiceId}/status?status=paid`);
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.delete(`/invoices/${invoiceToDelete.invoice_id}`);
      toast.success('Invoice deleted');
      fetchInvoices();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const downloadPDF = async (invoiceId, number) => {
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-slate-100 text-slate-700',
      'sent': 'bg-indigo-100 text-indigo-700',
      'paid': 'bg-emerald-100 text-emerald-700',
      'overdue': 'bg-rose-100 text-rose-700',
      'cancelled': 'bg-amber-100 text-amber-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-outfit">Invoice Management</h1>
          <p className="text-slate-600 mt-1 font-inter">Create, send, and track your business invoices</p>
        </div>
        <Button onClick={() => { setSelectedInvoice(null); setModalOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700">
          <FilePlus className="h-4 w-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-100">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by invoice number or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200"
              />
            </div>
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-slate-50 border-slate-200">
                  <div className="flex items-center">
                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Status" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice List */}
      <Card className="border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500 tracking-wider">Invoice #</th>
                <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500 tracking-wider">Issue Date</th>
                <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500 tracking-wider">Total Amount</th>
                <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500 tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold uppercase text-slate-500 tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400">No invoices found.</td>
                </tr>
              ) : (
                filteredInvoices.map((inv) => (
                  <tr key={inv.invoice_id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-900">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500 truncate max-w-[200px]">{inv.notes || 'No notes'}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(inv.issue_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-900">{formatCurrency(inv.total_amount, inv.currency)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(inv.status)}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedInvoice(inv); setModalOpen(true); }} title="Edit" className="text-slate-600">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => downloadPDF(inv.invoice_id, inv.invoice_number)} title="Download PDF" className="text-slate-600">
                          <Download className="h-4 w-4" />
                        </Button>
                        {inv.status !== 'paid' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleSendEmail(inv.invoice_id)} title="Send Email" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50">
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleMarkAsPaid(inv.invoice_id)} title="Mark as Paid" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => { setInvoiceToDelete(inv); setDeleteDialogOpen(true); }} title="Delete" className="text-rose-400 hover:text-rose-600 hover:bg-rose-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <InvoiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        invoice={selectedInvoice}
        onSuccess={fetchInvoices}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice {invoiceToDelete?.invoice_number}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Invoices;
